import {User} from "../modules/auth/models"
import DateUtils from "./DateUtils"

/**
 * Formatting helpers for the Modern skin, ported from the prototype's
 * `lib/format.js` and adapted to the production `User` model.
 *
 * Deliberately avoids String.prototype.padStart / Object.fromEntries / flat —
 * tsconfig targets es5 and CRA 3.3 ships no polyfill for them here.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function pad2(n: number): string {
    return n < 10 ? "0" + n : String(n)
}

/** ISO string / date-ish -> "YYYY-MM-DD HH:mm". Returns an em dash on bad input. */
export function formatDateTime(value: string | number | Date | null | undefined): string {
    if (!value) {
        return "—"
    }

    const d = new Date(value as any);

    if (isNaN(d.getTime())) {
        // already a plain "YYYY-MM-DD HH:mm[:ss]" string
        return String(value).slice(0, 16).replace("T", " ")
    }

    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/**
 * Long readable form: "25 Mar 2026 · 14:30:22".
 *
 * Routed through the same `DateUtils` pipeline the dashboard's date column
 * uses. Reading the raw value with local getters (as this did) put the record
 * page's header a full UTC offset away from the dashboard row for the SAME
 * record — at UTC+2 the list said 16:00 and the record page said 14:00. The
 * pipeline is quirky, but it is production's, and the two screens disagreeing
 * about when a trace was acquired is the worse failure.
 *
 * Falls back to the normalizing branch `formatDateTime` uses rather than
 * echoing the raw string: the API also emits "YYYY-MM-DD HH:mm:ss" with a space
 * separator, which Chrome parses and Safari/embedded WebKit rejects — so the
 * old fallback rendered a bare DB string on exactly the browsers this app is
 * read on.
 */
export function formatLong(value: string | number | Date | null | undefined): string {
    if (!value) {
        return ""
    }

    let d = new Date(value as any);

    if (false === isNaN(d.getTime())) {
        try {
            d = new Date(DateUtils.getDateISOString(String(value)))
        } catch (e) {
            // getDateISOString calls toISOString, which throws RangeError on an
            // unparseable value — keep the local-getter reading.
        }
    }

    if (isNaN(d.getTime())) {
        // Normalize "2024-01-17 14:30:22" (space separator), which older
        // WebKit will not parse.
        const normalized = new Date(String(value).replace(" ", "T"));

        if (isNaN(normalized.getTime())) {
            return String(value).slice(0, 19).replace("T", " ")
        }

        d = normalized
    }

    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} · ` +
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

/** Header subtitle: "Wed · 25 Mar 2026 · 14:51". */
export function formatDateSub(d: Date): string {
    return `${DAYS[d.getDay()]} · ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} · ` +
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

export function initials(user: User | null): string {
    if (!user) {
        return "··"
    }

    const f = (user.firstname || "").trim();
    const l = (user.lastname || "").trim();
    const a = (f.charAt(0) || "") + (l.charAt(0) || "");

    return (a || (user.email || user.username || "U").charAt(0)).toUpperCase()
}

/**
 * "Dr. Firstname Lastname", falling back through email -> username -> a
 * translated generic.
 *
 * BOTH the generic and the honorific are passed in so this stays i18n-free —
 * the prefix used to be a hardcoded "Dr.", which rendered "Доброго ранку,
 * Dr. Іван Петренко" under the Ukrainian locale.
 *
 * @param user
 * @param fallback translated generic, used when there is no name at all
 * @param title translated honorific ("Dr." / "Dott." / "Д-р")
 */
export function displayName(user: User | null, fallback: string = "Doctor", title: string = "Dr."): string {
    if (!user) {
        return fallback
    }

    const f = (user.firstname || "").trim();
    const l = (user.lastname || "").trim();
    const name = [f, l].filter(Boolean).join(" ");

    if (name) {
        return `${title} ${name}`
    }

    return user.email || user.username || fallback
}

export type GreetingKey = "good_morning" | "good_afternoon" | "good_evening"

export function greetingKey(hours: number): GreetingKey {
    if (hours < 12) {
        return "good_morning"
    }

    if (hours < 18) {
        return "good_afternoon"
    }

    return "good_evening"
}

/** "ECG_20240117_..." -> "20240117…" for the narrow Record Name column. */
export function shortRecordName(fileName: string | null | undefined): string {
    if (!fileName) {
        return "—"
    }

    const stripped = fileName.replace("ECG_", "");

    return stripped.length > 8 ? stripped.slice(0, 8) + "…" : stripped
}

/**
 * Deterministic 7-point eased ramp terminating at the real value.
 *
 * The production stats API exposes no historical series, so the sparkline is
 * explicitly decorative — but it is anchored to the true current value so it
 * can never visually contradict the number printed above it. If this is ever
 * judged too speculative for a clinical dashboard, deleting <Sparkline/> from
 * the stat card foot is a one-line change with no layout impact.
 */
const SPARK_CURVE = [0.62, 0.70, 0.66, 0.78, 0.83, 0.91, 1];

export function sparkFromValue(value: number): number[] {
    const v = typeof value === "number" && isFinite(value) ? value : 0;

    return SPARK_CURVE.map(f => Math.round(v * f))
}
